import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { isBookingConfigured } from '@/components/feature/bookingConfig';

export interface Timeslot { start_time: string; end_time: string; }

export interface BookingField {
  name: string;
  label: string;
  type: 'text' | 'tel' | 'email' | 'textarea';
  required?: boolean;
  placeholder?: string;
}

export interface BookingTexts {
  stepDate: string; stepTime: string; stepDetails: string;
  prevMonth: string; nextMonth: string;
  weekdays: string[]; months: string[];
  loading: string; loadError: string; retry: string; noSlots: string;
  selectTime: string; back: string; reviewTitle: string; dateLabel: string; timeLabel: string;
  modifyTime: string; submit: string; submitting: string; requiredError: string;
  successTitle: string; successBody: string; bookAnother: string; submitError: string;
  pickDateHint: string; noSlotsDay: string; continueLabel: string; selectedLabel: string;
  conflict: string;
  unavailableTitle: string; unavailableBody: string;
}

interface Props {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: BookingTexts;
  fields: BookingField[];
  slotMinutes: number;
  containerStyle?: CSSProperties;
}

const pad = (n: number) => String(n).padStart(2, '0');
const datePart = (s: string) => s.split(' ')[0];
const hm = (s: string) => (s.split(' ')[1] || '').slice(0, 5);
const toMin = (s: string) => {
  const [h, m] = hm(s).split(':').map(Number);
  return h * 60 + m;
};
const fromMin = (d: string, min: number) => `${d} ${pad(Math.floor(min / 60))}:${pad(min % 60)}:00`;
const DIRECT = ['customer_name', 'customer_phone'];

/** The calendar API returns availability windows (e.g. 09:00–17:00); split them into bookable slots. */
const splitWindows = (windows: Timeslot[], minutes: number, nowKey: string): Timeslot[] => {
  const out: Timeslot[] = [];
  windows.forEach((w) => {
    const d = datePart(w.start_time);
    if (d !== datePart(w.end_time)) return;
    const end = toMin(w.end_time);
    for (let t = toMin(w.start_time); t + minutes <= end; t += minutes) {
      const start = fromMin(d, t);
      if (start > nowKey) out.push({ start_time: start, end_time: fromMin(d, t + minutes) });
    }
  });
  return out;
};

export function BookingCalendar(props: Props) {
  // Fail closed: without a valid, owner-specific configuration nothing is fetched or submitted.
  if (!isBookingConfigured(props.timeslotsApi, props.appointmentsApi)) {
    const { texts, containerStyle } = props;
    return (
      <div style={containerStyle} role="status" className="w-full bg-white border border-background-300 rounded-lg p-6 md:p-10 text-foreground-950 text-center">
        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-xl"><i className="ri-calendar-line"></i></div>
        <p className="mt-4 font-heading text-xl">{texts.unavailableTitle}</p>
        <p className="mt-2 text-sm text-foreground-700 max-w-md mx-auto">{texts.unavailableBody}</p>
      </div>
    );
  }
  return <ConfiguredCalendar {...props} />;
}

function ConfiguredCalendar({ timeslotsApi, appointmentsApi, texts, fields, slotMinutes, containerStyle }: Props) {
  const [windows, setWindows] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [viewMonth, setViewMonth] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [booked, setBooked] = useState<Timeslot | null>(null);

  const nowKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  }, []);

  const fetchWindows = useCallback(async (): Promise<Timeslot[] | null> => {
    try {
      const res = await fetch(timeslotsApi, { cache: 'no-store' });
      if (!res.ok) throw new Error('fail');
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json?.data ?? json?.timeslots ?? []);
      const clean: Timeslot[] = Array.isArray(list) ? list.filter((s: Timeslot) => s?.start_time && s?.end_time) : [];
      setWindows(clean);
      setLoadError(false);
      return clean;
    } catch {
      setLoadError(true);
      return null;
    }
  }, [timeslotsApi]);

  const load = useCallback(async () => {
    setLoading(true);
    await fetchWindows();
    setLoading(false);
  }, [fetchWindows]);

  useEffect(() => { load(); }, [load]);

  const slots = useMemo(() => splitWindows(windows, slotMinutes, nowKey), [windows, slotMinutes, nowKey]);
  const availableDates = useMemo(() => Array.from(new Set(slots.map((s) => datePart(s.start_time)))).sort(), [slots]);

  useEffect(() => {
    if (availableDates.length && !viewMonth) setViewMonth(availableDates[0].slice(0, 7));
  }, [availableDates, viewMonth]);

  const grid = useMemo(() => {
    if (!viewMonth) return [];
    const [y, m] = viewMonth.split('-').map(Number);
    const cells: (string | null)[] = Array(new Date(y, m - 1, 1).getDay()).fill(null);
    for (let i = 1; i <= new Date(y, m, 0).getDate(); i += 1) cells.push(`${viewMonth}-${pad(i)}`);
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [viewMonth]);

  const shiftMonth = (delta: number) => {
    if (!viewMonth) return;
    const [y, m] = viewMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setViewMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  };

  const daySlots = slots.filter((s) => selectedDate && datePart(s.start_time) === selectedDate);
  const slotLabel = (s: Timeslot) => `${hm(s.start_time)} – ${hm(s.end_time)}`;

  const pickDate = (d: string) => {
    setSelectedDate(d);
    setSelectedSlot(null);
    setNotice('');
    setBooked(null);
    setStep(1);
  };

  const stillFree = (list: Timeslot[], s: Timeslot) => list.some((w) => w.start_time <= s.start_time && w.end_time >= s.end_time);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSlot || submitting) return;
    const data = new FormData(e.currentTarget);
    const val = (k: string) => String(data.get(k) ?? form[k] ?? '').trim();
    if (fields.some((f) => f.required && !val(f.name))) { setFormError(texts.requiredError); return; }
    setSubmitting(true);
    setFormError('');
    try {
      const fresh = await fetchWindows();
      if (fresh && !stillFree(fresh, selectedSlot)) {
        setSelectedSlot(null);
        setNotice(texts.conflict);
        setStep(1);
        return;
      }
      const extensions: Record<string, string> = {};
      fields.forEach((f) => { if (!DIRECT.includes(f.name) && f.name !== 'notes' && val(f.name)) extensions[f.name] = val(f.name); });
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: val('customer_name'),
          customer_phone: val('customer_phone'),
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: JSON.stringify({ text: val('notes'), extensions: JSON.stringify(extensions) }),
        }),
      });
      const txt = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(txt); } catch { parsed = null; }
      if (!res.ok || parsed?.code !== 'OK') {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
        if (res.status === 409 || /conflict|occupied|unavailable|booked/i.test(msg)) {
          setSelectedSlot(null);
          setNotice(texts.conflict);
          setStep(1);
          await fetchWindows();
        } else {
          setFormError(msg || texts.submitError);
        }
        return;
      }
      setBooked(selectedSlot);
      setForm({});
      await fetchWindows();
    } catch {
      setFormError(texts.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setBooked(null); setSelectedDate(null); setSelectedSlot(null); setStep(0); setNotice(''); };

  const steps = [texts.stepDate, texts.stepTime, texts.stepDetails];
  const inputCls = 'w-full box-border rounded-sm border border-background-300 bg-white px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:border-foreground-950';
  const ready = !loading && !loadError && availableDates.length > 0;

  return (
    <div style={containerStyle} className="w-full bg-white border border-background-300 rounded-lg p-5 md:p-8 text-foreground-950">
      <ol className="flex flex-wrap items-center gap-2 mb-6">
        {steps.map((s, i) => {
          const done = booked || i < step;
          const active = !booked && i === step;
          return (
            <li key={s} className="flex items-center gap-2">
              <span className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] uppercase tracking-[0.12em] whitespace-nowrap ${active ? 'bg-foreground-950 text-white' : done ? 'bg-background-100 text-foreground-950' : 'text-foreground-500'}`}>
                <span className="w-4 h-4 flex items-center justify-center">{done ? <i className="ri-check-line"></i> : i + 1}</span>
                {s}
              </span>
              {i < 2 && <span className="w-4 h-px bg-background-300" />}
            </li>
          );
        })}
      </ol>

      {loading && <p className="text-sm text-foreground-600 flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>{texts.loading}</p>}
      {!loading && loadError && !booked && (
        <div className="text-sm">
          <p className="text-foreground-700">{texts.loadError}</p>
          <button type="button" onClick={load} className="mt-3 rounded-sm border border-foreground-950 px-4 py-2 whitespace-nowrap cursor-pointer">{texts.retry}</button>
        </div>
      )}
      {!loading && !loadError && !availableDates.length && !booked && <p className="text-sm text-foreground-600">{texts.noSlots}</p>}

      {booked && (
        <div role="status" className="rounded-md bg-background-100 p-6 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-foreground-950 text-white text-xl"><i className="ri-check-line"></i></div>
          <p className="mt-4 font-heading text-xl">{texts.successTitle}</p>
          <p className="mt-2 text-sm">{texts.dateLabel}: <strong>{datePart(booked.start_time)}</strong> · {texts.timeLabel}: <strong>{slotLabel(booked)}</strong></p>
          <p className="mt-2 text-sm text-foreground-700">{texts.successBody}</p>
          <button type="button" onClick={reset} className="mt-5 rounded-sm border border-foreground-950 px-5 py-2.5 text-sm whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-white">{texts.bookAnother}</button>
        </div>
      )}

      {!booked && ready && step < 2 && viewMonth && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 lg:gap-8">
          <div className="min-w-0">
            <div className="flex items-center justify-between mb-4">
              <button type="button" aria-label={texts.prevMonth} onClick={() => shiftMonth(-1)} className="w-9 h-9 flex items-center justify-center rounded-sm border border-background-300 cursor-pointer hover:bg-background-100"><i className="ri-arrow-left-s-line"></i></button>
              <p className="font-heading text-lg">{texts.months[Number(viewMonth.slice(5, 7)) - 1]} {viewMonth.slice(0, 4)}</p>
              <button type="button" aria-label={texts.nextMonth} onClick={() => shiftMonth(1)} className="w-9 h-9 flex items-center justify-center rounded-sm border border-background-300 cursor-pointer hover:bg-background-100"><i className="ri-arrow-right-s-line"></i></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {texts.weekdays.map((w) => <span key={w} className="text-center text-[10px] uppercase tracking-wider text-foreground-500">{w}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const avail = availableDates.includes(d);
                const sel = d === selectedDate;
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={!avail}
                    aria-pressed={sel}
                    onClick={() => pickDate(d)}
                    className={`h-10 md:h-11 rounded-md text-sm transition-colors ${sel ? 'bg-foreground-950 text-white font-semibold cursor-pointer' : avail ? 'bg-background-100 border border-background-300 hover:border-foreground-950 cursor-pointer' : 'text-foreground-300 line-through cursor-not-allowed'}`}
                  >
                    {Number(d.slice(8, 10))}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-w-0 lg:border-l lg:border-background-200 lg:pl-8">
            {notice && <p role="alert" className="mb-4 rounded-md bg-background-100 border border-foreground-950 p-3 text-sm">{notice}</p>}
            {!selectedDate && <p className="text-sm text-foreground-600">{texts.pickDateHint}</p>}
            {selectedDate && (
              <>
                <p className="text-[12px] uppercase tracking-[0.14em] text-foreground-600 mb-3">{texts.selectTime} — {selectedDate}</p>
                {daySlots.length === 0 && <p className="text-sm text-foreground-600">{texts.noSlotsDay}</p>}
                <div className="grid grid-cols-2 gap-2">
                  {daySlots.map((s) => {
                    const sel = selectedSlot?.start_time === s.start_time;
                    return (
                      <button
                        key={s.start_time}
                        type="button"
                        aria-pressed={sel}
                        onClick={() => { setSelectedSlot(s); setNotice(''); }}
                        className={`rounded-sm border px-3 py-2.5 text-sm whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-1 ${sel ? 'bg-foreground-950 text-white border-foreground-950 font-semibold' : 'border-background-300 hover:border-foreground-950'}`}
                      >
                        {sel && <i className="ri-check-line"></i>}{slotLabel(s)}
                      </button>
                    );
                  })}
                </div>
                {selectedSlot && (
                  <div className="mt-5 rounded-md bg-background-100 p-4">
                    <p className="text-sm">{texts.selectedLabel}: <strong>{selectedDate} · {slotLabel(selectedSlot)}</strong></p>
                    <button type="button" onClick={() => setStep(2)} className="mt-3 w-full rounded-sm bg-foreground-950 text-white px-6 py-3 text-sm whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                      {texts.continueLabel}<i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!booked && step === 2 && selectedSlot && (
        <form id="nova-booking-form" onSubmit={submit} noValidate>
          <div className="rounded-md bg-background-100 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p className="text-[12px] uppercase tracking-[0.14em] text-foreground-600">{texts.reviewTitle}</p>
              <p className="mt-1">{texts.dateLabel}: <strong>{datePart(selectedSlot.start_time)}</strong></p>
              <p>{texts.timeLabel}: <strong>{slotLabel(selectedSlot)}</strong></p>
            </div>
            <button type="button" disabled={submitting} onClick={() => setStep(1)} className="rounded-sm border border-foreground-950 px-4 py-2 text-sm whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-white">{texts.modifyTime}</button>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <label key={f.name} className={`min-w-0 flex flex-col gap-2 text-sm ${f.type === 'textarea' ? 'sm:col-span-2' : ''}`}>
                <span className="text-foreground-700">{f.label}{f.required ? ' *' : ''}</span>
                {f.type === 'textarea' ? (
                  <textarea name={f.name} maxLength={500} rows={3} placeholder={f.placeholder} value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={inputCls} />
                ) : (
                  <input name={f.name} type={f.type} required={f.required} placeholder={f.placeholder} value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={inputCls} />
                )}
              </label>
            ))}
          </div>
          {formError && <p role="alert" className="mt-4 text-sm text-foreground-950 bg-background-100 border border-foreground-950 rounded-md p-3">{formError}</p>}
          <button type="submit" disabled={submitting} className="mt-6 w-full sm:w-auto rounded-sm bg-foreground-950 text-white px-8 py-4 text-sm whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2">
            {submitting && <i className="ri-loader-4-line animate-spin"></i>}
            {submitting ? texts.submitting : texts.submit}
          </button>
        </form>
      )}
    </div>
  );
}

export default BookingCalendar;